import { FC, useEffect, useState } from "react";
import { ArrayField, useFormContext } from "react-hook-form";
import { DsmButton, DsmGrid, DsmIcon } from "@dsm-dcs/design-system-react";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import ReactHookDsmSelect from "../../../../../modules/Helpers/ReactHookDsmSelect2";
import {
  availableMmsTypeForMonthlyStoragePeriods,
  availableMmsTypeForOverOrUnderStoragePeriod,
} from "../../../../models/Baseline/DairyV2Baseline";
import {
  CattleHousingAndManure as CattleHousingAndManureBaseline,
  ManurePhase,
  StoragePeriod,
} from "../../../../models/Baseline/CattleBaseline";
import { FormType } from "../../common";
import {
  CattleHousing,
  CattleManureManagementSystemType,
} from "../../../../models/Intervention/CattleIntervention";
import RowSelectWithMetricsV2 from "../../../../../modules/Farms/Intervention/RowSelectWithMetricsV2";
import { Spacer } from "../../../helpers/Spacer";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";
import { enumToOptionsArrayWithTranslatedStrings } from "../../../../utils/obj-utils";
import { v4 as uuidv4 } from "uuid";
import SustellMMSAdditionalItem from "../../../../../modules/Helpers/SustellMMSAdditionalItem";

type BeefHousingMMSComponentProps = {
  formType: FormType;
  isViewMode?: boolean;
  openDescriptionDialog: () => void;
  fieldItemPrefix: string;
  baseline: CattleHousingAndManureBaseline;
  stageIndex?: number;
  displayMMSRows?: boolean;
  mmsCollection: {
    mmsAdditions: Partial<ArrayField<Record<string, any>, "keyId">>[];
    appendMMSAdditions: (value: Partial<Record<string, any>> | Partial<Record<string, any>>[], shouldFocus?: boolean) => void;
    removeMMSAdditions: (index?: number | number[]) => void;
  }
};

type MMSInput = {
  mmsType: string;
  storagePeriod: string;
  share: string;
  id: string;
};

const BeefHousingMMSComponent: FC<BeefHousingMMSComponentProps> = ({
  formType = FormType.Add,
  isViewMode = false,
  fieldItemPrefix,
  baseline,
  stageIndex = 0,
  openDescriptionDialog,
  displayMMSRows = true,
  mmsCollection,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const baseItemPrefix = `${fieldItemPrefix}.manureSystems`;
  const fc = useFormContext<CattleHousing>();
  const [, forceUpdate] = useState({});
  const { mmsAdditions, appendMMSAdditions, removeMMSAdditions } = mmsCollection;

  const getMMSTypeOptions =
    enumToOptionsArrayWithTranslatedStrings(
      CattleManureManagementSystemType,
      intl,
      "SUSTELL.ENUMS.MMS"
    );

  const getMMSStorageOptions =
    enumToOptionsArrayWithTranslatedStrings(
      StoragePeriod,
      intl,
      "SUSTELL.BEEF.ENUMS.MMS_STORAGE"
    );

  const getAllSelectedMms = (): Array<MMSInput> => {
    const manureSystems: Array<MMSInput> = fc.getValues(`${fieldItemPrefix}.manureSystems`) || [];
    const manureSystemsAdditions: Array<MMSInput> = fc.getValues(`${fieldItemPrefix}.manureSystemsAdditions`) || [];

    return [...manureSystems, ...manureSystemsAdditions];
  };

  const isSingleMms = () => getAllSelectedMms().length === 1;
  const isMmsEmpty = () => getAllSelectedMms().length === 0;

  const isStorageFieldRequired = (
    controlMmsType: CattleManureManagementSystemType,
    baselineMmsType: string
  ): boolean => {
    const allAvailableMmsTypes = new Set([
      ...availableMmsTypeForMonthlyStoragePeriods,
      ...availableMmsTypeForOverOrUnderStoragePeriod,
    ]);

    if (isFieldFilled(controlMmsType)) {
      return allAvailableMmsTypes.has(controlMmsType);
    }

    return allAvailableMmsTypes.has(baselineMmsType as CattleManureManagementSystemType);
  };

  const mmsSelectHandler = (type: CattleManureManagementSystemType, index: number) => {
    if (type === CattleManureManagementSystemType.DailySpread) {
      fc.setValue(`${baseItemPrefix}.${index}.manurePhase`, ManurePhase.LiquidSlurry);
    } else {
      fc.setValue(`${baseItemPrefix}.${index}.manurePhase`, undefined);
    }

    if (!isStorageFieldRequired(type, ""))
      fc.setValue(`${baseItemPrefix}.${index}.storagePeriod`, "")

    forceUpdate({});
  };

  const getCorrectPeriodOptions = (index: number, baselineMmsType: string) => {
    let mmsType: CattleManureManagementSystemType = fc.getValues(`${baseItemPrefix}.${index}.mmsType`);
    if (!isFieldFilled(mmsType))
      mmsType = baselineMmsType as CattleManureManagementSystemType;

    const underOverPeriod: string[] = [
      StoragePeriod.UNDER_ONE_MONTH,
      StoragePeriod.OVER_ONE_MONTH
    ];

    const monthlyPeriod: string[] = [
      StoragePeriod.ONE_MONTH,
      StoragePeriod.THREE_MONTHS,
      StoragePeriod.FOUR_MONTHS,
      StoragePeriod.SIX_MONTHS,
      StoragePeriod.TWELVE_MONTHS,
    ]

    if (availableMmsTypeForOverOrUnderStoragePeriod.includes(mmsType))
      return getMMSStorageOptions.filter((opt) => underOverPeriod.includes(opt.value));

    return getMMSStorageOptions.filter((opt) => monthlyPeriod.includes(opt.value));
  };

  const showOriginalValue = (controlValue: string, baselineValue: string) => {
    if (!isFieldFilled(controlValue)) {
      return false;
    }

    return controlValue !== baselineValue;
  };

  return (
    <div>
      <p className={classes.headingTitleVariant} style={{ paddingTop: 0 }}>
        {intl.formatMessage({
          id: "SUSTELL.STAGE.BEEF.MMS.TIME_SPENT_IN_HOUSING.DESCRIPTION",
        })}
      </p>

      {displayMMSRows && baseline?.manureSystems && baseline?.manureSystems.map((item, index) => (
        <DsmGrid className={classes.dsmGridThreeColumnFirstBigOthersSmall}>
          {isViewMode ? (
            <>
              {showOriginalValue(
                fc.getValues(`${baseItemPrefix}.${index}.mmsType`) as string,
                item.mmsType as string) ? (
                <RowSelectWithMetricsV2
                  name={`${baseItemPrefix}.${index}.mmsType`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.MMS.MMS_TYPE' })}
                  tooltip={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.MMS.MMS_TYPE.TOOLTIP",
                  })}
                  options={getMMSTypeOptions}
                  baseline={item.mmsType}
                  disabled
                />
              ) : (
                <ReactHookDsmSelect
                  label={intl.formatMessage({
                    id: 'SUSTELL.STAGE.BEEF.MMS.MMS_TYPE'
                  })}
                  name={`${baseItemPrefix}.${index}.originalMmsType`}
                  tooltip={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.MMS.MMS_TYPE.TOOLTIP",
                  })}
                  options={getMMSTypeOptions}
                  defaultValue={item.mmsType}
                  disabled
                />
              )}
              {isStorageFieldRequired(fc.getValues(`${baseItemPrefix}.${index}.mmsType`), item.mmsType ?? "") ?
                (showOriginalValue(
                  fc.getValues(`${baseItemPrefix}.${index}.storagePeriod`) as string,
                  item.storagePeriod as string) ? (
                  <RowSelectWithMetricsV2
                    name={`${baseItemPrefix}.${index}.storagePeriod`}
                    label={intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.MMS.MMS_STORAGE_PERIOD' })}
                    options={getMMSStorageOptions}
                    baseline={item.storagePeriod}
                    disabled
                    originalLabel={item.storagePeriod !== undefined}
                  />
                ) : (
                  <ReactHookDsmSelect
                    name={`${baseItemPrefix}.${index}.originalStoragePeriod`}
                    label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.MMS.MMS_STORAGE_PERIOD' })}
                    options={getMMSStorageOptions}
                    defaultValue={item.storagePeriod}
                    disabled
                  />
                )) : (<div />)}
              {!isSingleMms() &&
                <ReactHookDsmInput
                  name={`${baseItemPrefix}.${index}.originalShare`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.MMS.MMS_SHARE' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.MMS.MMS_SHARE.TOOLTIP' })}
                  adornment="%"
                  defaultValue={item.share ?? ''}
                  type="number"
                  disabled
                />}
            </>
          ) : (
            <>
              <div>
                <RowSelectWithMetricsV2
                  name={`${baseItemPrefix}.${index}.mmsType`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.MMS.MMS_TYPE' })}
                  tooltip={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.MMS.MMS_TYPE.TOOLTIP",
                  })}
                  options={getMMSTypeOptions}
                  baseline={item.mmsType}
                  disabled={false}
                  changeHandler={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    mmsSelectHandler(e.target.value as CattleManureManagementSystemType, index)
                  }}
                />
                <Spacer />
              </div>
              <div>
                {isStorageFieldRequired(fc.getValues(`${baseItemPrefix}.[${index}].mmsType`), item.mmsType ?? "") &&
                  <>
                    <RowSelectWithMetricsV2
                      name={`${baseItemPrefix}.${index}.storagePeriod`}
                      label={intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.MMS.MMS_STORAGE_PERIOD' })}
                      options={getCorrectPeriodOptions(index, fc.getValues(`${baseItemPrefix}.${index}.mmsType`) ?? "")}
                      baseline={item.storagePeriod}
                      disabled={false}
                      originalLabel={item.storagePeriod !== undefined}
                    />
                    <Spacer />
                  </>
                }
              </div>
              <div>
                <ReactHookDsmInput
                  name={`${baseItemPrefix}.${index}.share`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.MMS.MMS_SHARE' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.MMS.MMS_SHARE.TOOLTIP' })}
                  adornment="%"
                  defaultValue={item.share ?? ''}
                  type="number"
                  disabled={false}
                />
                <Spacer />
              </div>
            </>
          )}
        </DsmGrid>
      ))}

      {mmsAdditions?.length > 0 && mmsAdditions?.map((item, index) => (
        <div key={item.keyId}>
          <div
            className={classes.dsmGridTwoColumn}
            style={{
              marginTop: '10px',
              marginBottom: '10px',
            }}
          >
            <SustellMMSAdditionalItem
              formType={formType}
              stageIndex={stageIndex}
              index={index}
              mmsOptions={getMMSTypeOptions}
              storageOptions={getMMSStorageOptions}
              isViewMode={formType === FormType.View || isViewMode}
              fieldItemPrefix={`${fieldItemPrefix}.manureSystemsAdditions`}
              handleRemove={removeMMSAdditions}
              isIntervention={true}
            />
          </div>
        </div>
      ))}

      {!isViewMode && <DsmGrid
        className={classes.dsmGridTwoColumnAndIconBtn}
      >
        <div>
          <DsmButton
            variant="text"
            onClick={() => appendMMSAdditions({
              id: uuidv4(),
              mmsType: "",
              storagePeriod: "",
              share: isMmsEmpty() ? "100" : "",
            })}
            disabled={!displayMMSRows}
          >
            <DsmIcon slot="before" name="general/plus-circle" />
            {intl.formatMessage({
              id: "SUSTELL.STAGE.POULTRY.HOUSING.ADD_ANOTHER_MMS",
            })}
          </DsmButton>

          <DsmButton
            variant="text"
            onClick={openDescriptionDialog}
            style={{ marginLeft: "20px" }}
          >
            <DsmIcon slot="before" name="general/eye" />
            {intl.formatMessage({ id: "DATABASE_FOUNDATION_DEFINITIONS" })}
          </DsmButton>
        </div>
      </DsmGrid>}
    </div>
  );
};

BeefHousingMMSComponent.defaultProps = {
  isViewMode: false,
};

export default BeefHousingMMSComponent;
